import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import {
  Target, TrendingUp, DollarSign, Users, Clock, ShieldCheck,
  Loader2, RefreshCw, Bell, BellOff, Mail, Send, History,
  Settings, AlertTriangle, ChevronDown, ChevronUp, Pause, Play
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => {
  if (!v && v !== 0) return '0';
  if (v >= 1e9) return `${(v/1e9).toFixed(1)}Md`;
  if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v/1e3).toFixed(0)}K`;
  return v.toLocaleString('fr-FR');
};

const LEVEL_COLORS = ['#3b82f6', '#f59e0b', '#f97316', '#ef4444'];
const LEVEL_LABELS = ['Rappel', '1ère Relance', '2ème Relance', 'Relance Finale'];

// ─── OBJECTIVES PROGRESS ──────────────────────────────────
const GaugeRing = ({ percent, size = 72, label, sub, color }) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(percent || 0, 100);
  const offset = circ - (pct / 100) * circ;
  const fill = pct >= 90 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f3f4f6" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color || fill} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-base font-bold" style={{ color: color || fill }}>{Math.round(pct)}%</span>
      </div>
      <p className="text-xs font-medium text-gray-700 mt-1">{label}</p>
      <p className="text-xs text-gray-400">{sub}</p>
    </div>
  );
};

const ObjectivesPanel = ({ token }) => {
  const [progress, setProgress] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchData = useCallback(async () => {
    try {
      const [pRes, oRes] = await Promise.all([
        axios.get(`${API}/admin/objectives/progress`, { headers }),
        axios.get(`${API}/admin/objectives`, { headers }),
      ]);
      setProgress(pRes.data);
      setObjectives(oRes.data);
      setFormData(oRes.data);
    } catch { } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveObjectives = async () => {
    try {
      await axios.put(`${API}/admin/objectives`, formData, { headers });
      toast({ title: 'Objectifs sauvegardés' });
      setEditing(false);
      fetchData();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const updateField = (section, key, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: Number(value) || 0 }
    }));
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!progress) return null;

  const { arr, revenue, operations, collection, new_clients } = progress;

  return (
    <div className="space-y-4" data-testid="objectives-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <Target className="h-4 w-4 text-emerald-600" />Objectifs & ARR
        </h3>
        <div className="flex gap-2">
          <button onClick={() => setEditing(!editing)} className="text-xs px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 flex items-center gap-1">
            <Settings className="h-3 w-3" />{editing ? 'Annuler' : 'Configurer'}
          </button>
          {editing && (
            <button onClick={saveObjectives} className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid="save-objectives-btn">
              Sauvegarder
            </button>
          )}
        </div>
      </div>

      {/* ARR Card */}
      <Card className="border border-emerald-200 bg-emerald-50/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-emerald-600 uppercase">ARR (Revenus Récurrents Annuels)</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1" style={{ fontFamily: 'monospace' }}>{fmt(arr?.totalAnnual)} FCFA</p>
              <p className="text-xs text-emerald-500 mt-0.5">{fmt(arr?.totalMonthly)} FCFA / mois · {arr?.contracts?.length || 0} contrat(s) actif(s)</p>
            </div>
            <TrendingUp className="h-8 w-8 text-emerald-300" />
          </div>
          {arr?.contracts?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-200 space-y-1">
              {arr.contracts.map(c => (
                <div key={c.contract} className="flex justify-between text-xs">
                  <span className="text-gray-600">{c.client} <span className="text-gray-400">({c.contract})</span></span>
                  <span className="font-mono font-semibold text-gray-800">{fmt(c.monthlyRevenue)}/mois</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gauges Grid */}
      {!editing ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="border border-gray-100">
            <CardContent className="p-3 flex flex-col items-center relative">
              <GaugeRing percent={revenue?.monthly?.percent || 0} label="CA Mensuel"
                sub={`${fmt(revenue?.monthly?.actual)} / ${fmt(revenue?.monthly?.target)}`} />
            </CardContent>
          </Card>
          <Card className="border border-gray-100">
            <CardContent className="p-3 flex flex-col items-center relative">
              <GaugeRing percent={operations?.monthly?.percent || 0} label="Opérations/Mois"
                sub={`${operations?.monthly?.actual || 0} / ${operations?.monthly?.target || '—'}`} />
            </CardContent>
          </Card>
          <Card className="border border-gray-100">
            <CardContent className="p-3 flex flex-col items-center relative">
              <GaugeRing percent={collection?.rate?.percent || 0} label="Tx Recouvrement"
                sub={`${collection?.rate?.actual || 0}% / ${collection?.rate?.target || 95}%`} />
            </CardContent>
          </Card>
          <Card className="border border-gray-100">
            <CardContent className="p-3 flex flex-col items-center relative">
              <GaugeRing percent={collection?.dso?.on_target ? 100 : 50} label="DSO"
                sub={`${collection?.dso?.actual || 0}j / ${collection?.dso?.target || 15}j`}
                color={collection?.dso?.on_target ? '#10b981' : '#ef4444'} />
            </CardContent>
          </Card>
          <Card className="border border-gray-100">
            <CardContent className="p-3 flex flex-col items-center relative">
              <GaugeRing percent={new_clients?.monthly?.percent || 0} label="Nvx Clients"
                sub={`${new_clients?.monthly?.actual || 0} / ${new_clients?.monthly?.target || '—'}`} />
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Editing Form */
        <Card className="border border-gray-200">
          <CardContent className="p-4 space-y-4">
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">CA (FCFA)</p>
              <div className="grid grid-cols-4 gap-2">
                {['weekly','monthly','quarterly','yearly'].map(k => (
                  <div key={k}>
                    <label className="text-xs text-gray-500 capitalize">{k === 'weekly' ? 'Semaine' : k === 'monthly' ? 'Mois' : k === 'quarterly' ? 'Trimestre' : 'Année'}</label>
                    <input type="number" value={formData?.revenue?.[k] || ''} onChange={e => updateField('revenue', k, e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5" placeholder="0" />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">Opérations</p>
              <div className="grid grid-cols-4 gap-2">
                {['daily','weekly','monthly','yearly'].map(k => (
                  <div key={k}>
                    <label className="text-xs text-gray-500">{k === 'daily' ? 'Jour' : k === 'weekly' ? 'Semaine' : k === 'monthly' ? 'Mois' : 'Année'}</label>
                    <input type="number" value={formData?.operations?.[k] || ''} onChange={e => updateField('operations', k, e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5" placeholder="0" />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Recouvrement</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Taux cible (%)</label>
                    <input type="number" value={formData?.collection?.target_rate_percent || ''} onChange={e => updateField('collection', 'target_rate_percent', e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">DSO cible (jours)</label>
                    <input type="number" value={formData?.collection?.target_dso_days || ''} onChange={e => updateField('collection', 'target_dso_days', e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5" />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Nouveaux Clients</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Mois</label>
                    <input type="number" value={formData?.new_clients?.monthly || ''} onChange={e => updateField('new_clients', 'monthly', e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Trimestre</label>
                    <input type="number" value={formData?.new_clients?.quarterly || ''} onChange={e => updateField('new_clients', 'quarterly', e.target.value)}
                      className="w-full border rounded px-2 py-1.5 text-xs font-mono mt-0.5" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── REMINDER CONFIG & LOGS ────────────────────────────────
const ReminderPanel = ({ token }) => {
  const [config, setConfig] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeView, setActiveView] = useState('overdue');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchData = useCallback(async () => {
    try {
      const [cRes, oRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/reminders/config`, { headers }),
        axios.get(`${API}/admin/reminders/overdue`, { headers }),
        axios.get(`${API}/admin/reminders/logs?limit=50`, { headers }),
      ]);
      setConfig(cRes.data);
      setOverdue(oRes.data);
      setLogs(lRes.data);
    } catch { } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleEnabled = async () => {
    const newConfig = { ...config, enabled: !config.enabled };
    try {
      const res = await axios.put(`${API}/admin/reminders/config`, newConfig, { headers });
      setConfig(res.data);
      toast({ title: res.data.enabled ? 'Relances activées' : 'Relances désactivées' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const updateLevel = async (idx, field, value) => {
    const newLevels = [...config.levels];
    newLevels[idx] = { ...newLevels[idx], [field]: field === 'trigger_days' ? Number(value) : value };
    const newConfig = { ...config, levels: newLevels };
    try {
      const res = await axios.put(`${API}/admin/reminders/config`, newConfig, { headers });
      setConfig(res.data);
    } catch { }
  };

  const runNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/reminders/run`, {}, { headers });
      const d = res.data;
      if (d.success) {
        toast({ title: `Cycle terminé : ${d.sent} relance(s) envoyée(s), ${d.skipped} ignorée(s)` });
      } else {
        toast({ title: d.error || 'Erreur', variant: 'destructive' });
      }
      fetchData();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSending(false); }
  };

  const updateInternalEmails = async (emailsStr) => {
    const emails = emailsStr.split(',').map(e => e.trim()).filter(Boolean);
    const newConfig = { ...config, internal_alert_emails: emails };
    try {
      const res = await axios.put(`${API}/admin/reminders/config`, newConfig, { headers });
      setConfig(res.data);
    } catch { }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!config) return null;

  return (
    <div className="space-y-4" data-testid="reminder-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <Bell className="h-4 w-4 text-emerald-600" />Relances Automatiques
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{config.enabled ? 'Activé' : 'Désactivé'}</span>
            <Switch checked={config.enabled} onCheckedChange={toggleEnabled} data-testid="reminder-toggle" />
          </div>
          <button onClick={runNow} disabled={sending}
            className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
            data-testid="run-reminders-btn">
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Lancer maintenant
          </button>
        </div>
      </div>

      {/* Levels Config */}
      <Card className="border border-gray-100">
        <CardContent className="p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-700">Niveaux de relance (tous courtois)</p>
          {config.levels?.map((lvl, idx) => (
            <div key={lvl.level} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div className="w-2 h-8 rounded-full" style={{ backgroundColor: LEVEL_COLORS[idx] }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900">{lvl.name}</p>
                <p className="text-xs text-gray-400">{lvl.email_subject}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">J</span>
                <input type="number" value={lvl.trigger_days}
                  onChange={e => updateLevel(idx, 'trigger_days', e.target.value)}
                  className="w-12 border rounded px-1.5 py-1 text-xs font-mono text-center" />
              </div>
              <Switch checked={lvl.active} onCheckedChange={v => updateLevel(idx, 'active', v)} />
            </div>
          ))}
          <div className="pt-2">
            <label className="text-xs text-gray-500">Emails alertes internes (séparés par virgule)</label>
            <input type="text" defaultValue={(config.internal_alert_emails || []).join(', ')}
              onBlur={e => updateInternalEmails(e.target.value)}
              className="w-full border rounded px-2 py-1.5 text-xs mt-1" placeholder="finance@baobabco.net" />
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Overdue / Logs */}
      <div className="flex gap-2 border-b border-gray-200 pb-1">
        <button onClick={() => setActiveView('overdue')}
          className={`text-xs px-3 py-1.5 rounded-t font-medium ${activeView === 'overdue' ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-gray-500'}`}>
          <AlertTriangle className="h-3 w-3 inline mr-1" />Factures impayées ({overdue.length})
        </button>
        <button onClick={() => setActiveView('logs')}
          className={`text-xs px-3 py-1.5 rounded-t font-medium ${activeView === 'logs' ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-gray-500'}`}>
          <History className="h-3 w-3 inline mr-1" />Historique ({logs.length})
        </button>
      </div>

      {activeView === 'overdue' ? (
        <Card className="border border-gray-100">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="overdue-table">
                <thead><tr className="bg-gray-50/50 border-b text-gray-500 uppercase tracking-wider">
                  <th className="text-left py-2.5 px-3 font-medium">Réf</th>
                  <th className="text-left py-2.5 px-3 font-medium">Client</th>
                  <th className="text-right py-2.5 px-3 font-medium">Reste Dû</th>
                  <th className="text-center py-2.5 px-3 font-medium">Échéance</th>
                  <th className="text-center py-2.5 px-3 font-medium">Retard</th>
                  <th className="text-center py-2.5 px-3 font-medium">Niveau</th>
                  <th className="text-center py-2.5 px-3 font-medium">Relances</th>
                </tr></thead>
                <tbody>
                  {overdue.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-400">Aucune facture impayée</td></tr>
                  ) : overdue.map(inv => {
                    const lvl = inv.last_reminder_level;
                    const daysAbs = Math.abs(inv.days_overdue);
                    const isOverdue = inv.days_overdue > 0;
                    return (
                      <tr key={inv.invoice_ref} className={`border-b border-gray-50 ${isOverdue ? (daysAbs > 30 ? 'bg-red-50/30' : daysAbs > 7 ? 'bg-orange-50/30' : '') : ''}`}>
                        <td className="py-2.5 px-3 font-mono font-medium">{inv.invoice_ref}</td>
                        <td className="py-2.5 px-3 text-gray-700">{inv.client_name}</td>
                        <td className="py-2.5 px-3 text-right font-mono font-semibold text-red-600">{fmt(inv.remaining)}</td>
                        <td className="py-2.5 px-3 text-center text-gray-600">{inv.due_date?.slice(0, 10)}</td>
                        <td className="py-2.5 px-3 text-center">
                          <Badge className={`text-xs font-mono ${isOverdue ? (daysAbs > 30 ? 'bg-red-100 text-red-700' : daysAbs > 7 ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700') : 'bg-green-100 text-green-700'}`}>
                            {isOverdue ? `+${daysAbs}j` : `${daysAbs}j`}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {lvl !== null && lvl !== undefined ? (
                            <span className="inline-block w-5 h-5 rounded-full text-white text-xs font-bold leading-5 text-center" style={{ backgroundColor: LEVEL_COLORS[lvl] }}>
                              {lvl}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono">{inv.reminder_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-100">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="reminder-logs-table">
                <thead><tr className="bg-gray-50/50 border-b text-gray-500 uppercase tracking-wider">
                  <th className="text-left py-2.5 px-3 font-medium">Date</th>
                  <th className="text-left py-2.5 px-3 font-medium">Réf</th>
                  <th className="text-left py-2.5 px-3 font-medium">Client</th>
                  <th className="text-center py-2.5 px-3 font-medium">Niveau</th>
                  <th className="text-right py-2.5 px-3 font-medium">Montant</th>
                  <th className="text-center py-2.5 px-3 font-medium">Statut</th>
                </tr></thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-8 text-gray-400">Aucune relance envoyée</td></tr>
                  ) : logs.map((log, i) => (
                    <tr key={log.id || i} className="border-b border-gray-50">
                      <td className="py-2 px-3 text-gray-500">{log.sent_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-2 px-3 font-mono">{log.invoice_ref}</td>
                      <td className="py-2 px-3">{log.client_name}</td>
                      <td className="py-2 px-3 text-center">
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white" style={{ backgroundColor: LEVEL_COLORS[log.level] }}>
                          {LEVEL_LABELS[log.level]}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono">{fmt(log.amount_due)}</td>
                      <td className="py-2 px-3 text-center">
                        {log.email_sent ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">Envoyé</Badge>
                        ) : (
                          <Badge variant="destructive" className="text-xs">Échec</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── EXPORTS ────────────────────────────────────────────
export { ReminderPanel };

const ObjectivesAndReminders = ({ token }) => {
  return (
    <div className="space-y-6" data-testid="objectives-reminders">
      <ObjectivesPanel token={token} />
    </div>
  );
};

export default ObjectivesAndReminders;
